import { useNavigate } from "react-router-dom";
import { 
  CheckCircle, 
  AlertCircle, 
  Loader2, 
  CreditCard, 
  Info, 
  UserPlus, 
  Users,
  Clock,
  ShieldCheck,
  XCircle,
  HandCoins,
  X
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import type { Notification, NotificationType } from "@/hooks/useNotifications";

interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead: (id: string) => void;
  onDelete: (id: string) => void;
  onClose: () => void;
}

const ICON_MAP: Record<NotificationType, { icon: typeof CheckCircle; className: string }> = {
  delivery_success: { icon: CheckCircle, className: "text-success" },
  delivery_complete: { icon: CheckCircle, className: "text-success" },
  delivery_failed: { icon: AlertCircle, className: "text-destructive" },
  processing: { icon: Loader2, className: "text-accent animate-spin" },
  billing: { icon: CreditCard, className: "text-accent-gold-foreground" },
  system: { icon: Info, className: "text-muted-foreground" },
  team_invite: { icon: UserPlus, className: "text-accent" },
  team_accepted: { icon: Users, className: "text-success" },
  user_pending_review: { icon: Clock, className: "text-accent-gold-foreground" },
  approval_pending: { icon: Clock, className: "text-accent-gold-foreground" },
  content_approved: { icon: ShieldCheck, className: "text-success" },
  content_rejected: { icon: XCircle, className: "text-destructive" },
  split_invite: { icon: HandCoins, className: "text-accent" },
  split_accepted: { icon: CheckCircle, className: "text-success" },
  split_declined: { icon: XCircle, className: "text-destructive" },
  collaborator_invite: { icon: UserPlus, className: "text-accent" },
  bug_report_new: { icon: AlertCircle, className: "text-destructive" },
  new_support_ticket: { icon: Info, className: "text-accent" },
  support_ticket_reply: { icon: Info, className: "text-accent" },
};

function formatTimeAgo(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export function NotificationItem({
  notification,
  onMarkAsRead,
  onDelete,
  onClose,
}: NotificationItemProps) {
  const navigate = useNavigate();
  const { icon: Icon, className: iconClassName } = ICON_MAP[notification.type] ?? ICON_MAP.system;

  const handleClick = () => {
    if (!notification.is_read) {
      onMarkAsRead(notification.id);
    }
    if (notification.link) {
      navigate(notification.link);
      onClose();
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(notification.id);
  };

  return (
    <div
      onClick={handleClick}
      className={cn(
        "group flex items-start gap-3 p-3 rounded-lg transition-colors",
        notification.link ? "cursor-pointer hover:bg-muted" : "cursor-default",
        !notification.is_read && "bg-accent/5"
      )}
    >
      {/* Icon */}
      <div className="flex-shrink-0 mt-0.5">
        <Icon className={cn("h-5 w-5", iconClassName)} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex items-start justify-between gap-2">
          <p className={cn(
            "text-sm line-clamp-1",
            !notification.is_read && "font-medium"
          )}>
            {notification.title}
          </p>
          {!notification.is_read && (
            <span className="flex-shrink-0 h-2 w-2 rounded-full bg-accent" />
          )}
        </div>
        <p className="text-xs text-muted-foreground line-clamp-2">
          {notification.message}
        </p>
        <p className="text-xs text-muted-foreground/70">
          {formatTimeAgo(notification.created_at)}
        </p>
      </div>

      {/* Delete button */}
      <Button
        variant="ghost"
        size="icon"
        className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
        onClick={handleDelete}
      >
        <X className="h-3.5 w-3.5" />
        <span className="sr-only">Dismiss</span>
      </Button>
    </div>
  );
}
